/* eslint formatjs/no-literal-string-in-jsx: "error" */
import React from "react"
import {
  Form,
  FormInput,
  FormButton,
  FlexContainer,
  H1,
  Text,
  Divider,
  Container,
  Button,
  Stack
} from "@carrotfertility/carotene"
import { Asterisk } from "../views/atoms/Atoms"
import { useCurrentUser } from "../context/user/UserContext"
import { emailValidationCarotene } from "../../utils/Regexes"
import { isHttpError } from "../../utils/HttpErrors"
import { CarrotErrorCodes, getErrorMessageFromCode } from "../../utils/CarrotErrors"
import { getHeap } from "../../utils/heap"
import useEmailVerification from "./useEmailVerification"
import { FormattedMessage, MessageDescriptor, useIntl } from "react-intl"
import { useIntlValidator } from "utils/hooks/useIntlValidator"

type AddEditEmailAddressModalProps = {
  title: string
  setShowActionTaken: (boolean) => void
  showRemoveEmail?: boolean
  onRemove?: (event: any) => Promise<void>
  removeDisabled?: boolean
}

export default function AddEditEmailAddress({
  title,
  setShowActionTaken,
  showRemoveEmail,
  onRemove,
  removeDisabled
}: AddEditEmailAddressModalProps): JSX.Element {
  const { email, personalEmail, partnerEmail } = useCurrentUser()
  const { updatePersonalEmail, sendVerificationEmail } = useEmailVerification()
  const intl = useIntl()
  const intlEmailValidation = useIntlValidator(emailValidationCarotene)

  function validateEmailIsDuplicateWithinAccount(
    newPersonalEmail,
    currentPersonalEmail,
    primaryEmail
  ): string | boolean | MessageDescriptor {
    const sanitizedNewPersonalEmail = newPersonalEmail.toLowerCase()
    if (
      sanitizedNewPersonalEmail === currentPersonalEmail ||
      sanitizedNewPersonalEmail === primaryEmail ||
      sanitizedNewPersonalEmail === partnerEmail
    ) {
      getHeap().track("DuplicateEmail", { EventName: "Attempted to use non-unique email" })
      return intl.formatMessage({ defaultMessage: "That email address is already associated with your account." })
    }
    return intlEmailValidation(newPersonalEmail)
  }

  async function addOrUpdate(formValues, instance): Promise<void> {
    const sanitizedPersonalEmail = formValues?.personalEmail.toLowerCase()
    if (formValues?.personalEmail) {
      try {
        await updatePersonalEmail(sanitizedPersonalEmail)
        await sendVerificationEmail()
        setShowActionTaken(true)
      } catch (err) {
        const { code } = await err.response.json()
        if (isHttpError(err)) {
          if (code === CarrotErrorCodes.PERSONAL_EMAIL_ALREADY_EXISTS) {
            instance.setFieldMeta("personalEmail", {
              error: intl.formatMessage({ defaultMessage: "That email address is already in use" })
            })
            getHeap().track("DuplicateEmail", { EventName: "Attempted to use non-unique email" })
            return
          }
        } else {
          const errorMessage = getErrorMessageFromCode(code)
          instance.setFieldMeta("personalEmail", {
            error: intl.formatMessage(errorMessage)
          })
        }
      }
    }
  }
  return (
    <>
      <FlexContainer paddingLeftRight={"huge"} direction={"column"} paddingTopBottom={"medium"}>
        <H1 color={"black80"} stack={"tiny"}>
          {title}
        </H1>
        <Text color={"black60"}>
          <FormattedMessage defaultMessage="We'll send a verification link to your inbox." />
        </Text>
      </FlexContainer>

      <Container stack={"medium"}>
        <Divider />
      </Container>

      <Form onSubmit={addOrUpdate}>
        <Container paddingLeftRight={"huge"} stack={"medium"}>
          <Container stack={"tiny"}>
            <Text weight={"semibold"}>
              <FormattedMessage defaultMessage="Email address" /> <Asterisk />
            </Text>
          </Container>
          <FormInput
            name="personalEmail"
            defaultValue={personalEmail}
            validate={(newPersonalEmail) =>
              validateEmailIsDuplicateWithinAccount(newPersonalEmail, personalEmail, email)
            }
          />
        </Container>
        <FlexContainer
          stack={"medium"}
          paddingLeftRight={"huge"}
          paddingTopBottom={"medium"}
          justifyContent={["center", "flex-end"]}
        >
          <Stack spacing={"small"} direction={["column", "row"]} alignItems={["center", "end"]}>
            {showRemoveEmail && (
              <Button variant={"secondary"} disabled={removeDisabled} onClick={(event) => onRemove(event)}>
                <FormattedMessage defaultMessage="Remove" />
              </Button>
            )}
            <FormButton>
              <FormattedMessage defaultMessage="Send Verification" />
            </FormButton>
          </Stack>
        </FlexContainer>
      </Form>
    </>
  )
}
